import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import * as styles from './PostSeriesInTopicList.module.scss';
import FloatingCard from '../../components/FloatingCard';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import seriesIcon from '../../icons/non-prefilled/16x16/series.svg';
import SeriesHoverCard from '../../series/SeriesHoverCard';
import PostTopicListItem from '../topic/PostTopicListItem';

const SERIES_FRAGMENT = gql(`
  fragment PostSeriesInTopicList_series on SeriesFields {
    id
    name
    posts {
      id
    }
  }
`);

const SERIES_DETAILS_FRAGMENT = gql(`
  fragment PostSeriesInTopicList_seriesDetails on SeriesFields {
    id
    ...SeriesHoverCard_series
  }
`);

interface PostSeriesInTopicListProps {
  series?: DocumentType<typeof SERIES_FRAGMENT>;
  seriesDetails?: DocumentType<typeof SERIES_DETAILS_FRAGMENT>;
  postId: string;
  disableHoverView?: boolean;
  size: ComponentProps<typeof PostTopicListItem.Root>['size'];
}

const PostSeriesInTopicList = ({
  series,
  seriesDetails,
  postId,
  disableHoverView,
  size,
}: PostSeriesInTopicListProps) => {
  const indexInSeries = series?.posts.findIndex((post) => post.id === postId);
  const [isPopOverOpen, setIsPopOverOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  if (series == null || indexInSeries == null || indexInSeries === -1) {
    return null;
  }

  const content = (
    <PostTopicListItem.Content
      setAnchorElement={disableHoverView ? undefined : setAnchorElement}
      icon={
        <SVGIcon className={styles.seriesIcon} aria-hidden>
          {seriesIcon}
        </SVGIcon>
      }
      name={
        size === 'small' ? (
          <>
            {series.name}
            <span className={styles.seriesIndex}>
              {indexInSeries + 1}/{series.posts.length}
            </span>
          </>
        ) : (
          series.name
        )
      }
      description={
        size === 'small'
          ? undefined
          : `Part ${indexInSeries + 1} of ${series.posts.length}`
      }
    />
  );

  return (
    <PostTopicListItem.Root size={size}>
      {disableHoverView ? (
        content
      ) : (
        <PostTopicListItem.PopOver
          anchorElement={anchorElement}
          open={isPopOverOpen}
          onOpenChange={setIsPopOverOpen}
          popOver={
            <FloatingCard className={styles.popOver}>
              <SeriesHoverCard
                postId={postId}
                series={seriesDetails}
                showEditButton
                onClickEditButton={() => setIsPopOverOpen(false)}
              />
            </FloatingCard>
          }
        >
          {content}
        </PostTopicListItem.PopOver>
      )}
    </PostTopicListItem.Root>
  );
};

export default PostSeriesInTopicList;
